import React, { useState, useEffect } from 'react';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom'; 
import './clients.css';
import Search from '../../components/Search/Search';
import ClientList from '../../components/ClientList/ClientList';
import ClientDetailsCard from '../../components/ClientDetailsCard/ClientDetailsCard';

const Clients = () => {
    const [isCardOpen, setIsCardOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortConfig, setSortConfig] = useState({
        field: '',
        direction: 'asc'
    });
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedClients, setSelectedClients] = useState(new Set());
    const [allClients, setAllClients] = useState([]);

    const navigate = useNavigate(); 

    useEffect(() => {
        localStorage.removeItem('clientsCurrentPage');
    }, []);

    const handleClientClick = (client) => {
        setSelectedClient(client);
        setIsCardOpen(true);
    };

    const closeCard = () => {
        setIsCardOpen(false);
    };

    const handleSearch = (query) => {
        setSearchQuery(query);
    };

    const handleStatusChange = (status) => {
        setSelectedStatus(status); 
    };

    const handleSortChange = (field, direction) => {
        setSortConfig({
            field,
            direction
        });
    };

    const handleNewClientClick = () => {
        navigate('/new-client-form');
    };

    const handleImportClientsClick = () => {
        navigate('/import-client');
    };

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleCheckboxChange = (clientId, isChecked) => {
        setSelectedClients(prev => {
            const newSet = new Set(prev);
            if (isChecked) {
                newSet.add(clientId);
            } else {
                newSet.delete(clientId);
            }
            return newSet;
        });
    };
    
    const handleSelectAll = (clientsArray) => {
        if (clientsArray.length > 0) {
            setSelectedClients(new Set(clientsArray.map(client => client.id)));
        } else {
            setSelectedClients(new Set());
        }
    };
    
    const handleBulkUpdateClick = () => {
        navigate('/bulk-update-clients', { state: { selectedClientsData: getSelectedClientsData() } });
    };

    const handleClientsLoad = (clientsData) => {
        setAllClients(clientsData);
    };

    const getSelectedClientsData = () => {
        return allClients.filter(client => selectedClients.has(client.id));
    };

    return (
        <Box className={`clients-container ${isCardOpen ? 'shrink' : ''}`} display="flex">
            <Box flex={1}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <h1>All Clients</h1>
                </Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Box flex={0.8}>
                        <Search 
                            onStatusChange={handleStatusChange} 
                            onSearch={handleSearch} 
                            onSortChange={handleSortChange}
                        />
                    </Box>
                    {selectedClients.size > 0 && (
                        <Button
                            className="bulk-update-button"
                            onClick={handleBulkUpdateClick}
                        >
                            Update {selectedClients.size} Selected Client{selectedClients.size > 1 ? 's' : ''}
                        </Button>
                    )}
                    <div>
                        <Button 
                            variant="contained" 
                            className="btn-new-client" 
                            onClick={handleMenuClick}
                            sx={{
                                backgroundColor: 'rgba(87, 42, 241, 1)',
                                color: 'white',
                                borderRadius: '12px',
                                padding: '0px 16px', 
                                fontFamily: 'Red Hat Text',
                                fontSize: '1.5rem',
                                textTransform: 'capitalize',
                                marginLeft: '-80%',
                                minWidth: '40px',
                                '&:hover': {
                                    backgroundColor: '#4c2de2',
                                    transform: 'translateY(-2px)',
                                },
                                '&:active': {
                                    backgroundColor: '#4321c2',
                                    transform: 'translateY(0)',
                                },
                            }}
                        >
                            +
                        </Button>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                        >
                            <MenuItem onClick={() => {
                                handleMenuClose();
                                handleNewClientClick();
                            }}>
                                New Client
                            </MenuItem>
                            <MenuItem onClick={() => {
                                handleMenuClose();
                                handleImportClientsClick();
                            }}>
                                Import Clients
                            </MenuItem>
                        </Menu>
                    </div>
                </Box>
                <Box sx={{ marginTop: '20px' }}>
                    <ClientList 
                        onClientClick={handleClientClick} 
                        selectedStatus={selectedStatus} 
                        searchQuery={searchQuery}
                        sortConfig={sortConfig}
                        selectedClients={selectedClients}
                        onCheckboxChange={handleCheckboxChange}
                        onSelectAll={handleSelectAll}
                        onClientsLoad={handleClientsLoad}
                    />
                </Box>
            </Box>

            {isCardOpen && (
                <ClientDetailsCard
                    client={selectedClient}
                    isOpen={isCardOpen}
                    onClose={closeCard}
                />
            )}
        </Box>
    );
};

export default Clients;